'use client';

import React from 'react';
import { cn } from '@sharpr/ui';
import { CloudRain, Wind, Snowflake, Sun, Home, CloudSun } from 'lucide-react';
import { pickSeverity, type WeatherSeverityLabel } from '@/services/weather/classify';
import type { GameWeather } from '@/services/weather/types';

function labelClasses(kind: WeatherSeverityLabel['kind']): string {
  switch (kind) {
    case 'indoor':     return 'bg-slate-500/10 text-slate-600 dark:text-slate-300';
    case 'rain':       return 'bg-red-500/10 text-red-600 dark:text-red-400';
    case 'wind-out':   return 'bg-amber-500/10 text-amber-600 dark:text-amber-400';
    case 'wind-in':    return 'bg-blue-500/10 text-blue-600 dark:text-blue-400';
    case 'wind-cross': return 'bg-purple-500/10 text-purple-600 dark:text-purple-400';
    case 'cold':       return 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400';
    case 'heat':       return 'bg-orange-500/10 text-orange-600 dark:text-orange-400';
    case 'clear':      return 'bg-muted/40 dark:bg-white/5 text-muted-foreground dark:text-text-tertiary';
  }
}

/** Text/icon color per severity — the compact pill uses the shared neutral
 *  button surface (border + bg), so severity is conveyed by color only. */
function severityTextClass(kind: WeatherSeverityLabel['kind']): string {
  switch (kind) {
    case 'indoor':     return 'text-slate-600 dark:text-slate-300';
    case 'rain':       return 'text-red-600 dark:text-red-400';
    case 'wind-out':   return 'text-amber-600 dark:text-amber-400';
    case 'wind-in':    return 'text-blue-600 dark:text-blue-400';
    case 'wind-cross': return 'text-purple-600 dark:text-purple-400';
    case 'cold':       return 'text-cyan-600 dark:text-cyan-400';
    case 'heat':       return 'text-orange-600 dark:text-orange-400';
    case 'clear':      return 'text-muted-foreground dark:text-text-tertiary';
  }
}

function LabelIcon({ kind, className }: { kind: WeatherSeverityLabel['kind']; className: string }) {
  switch (kind) {
    case 'indoor':     return <Home className={className} />;
    case 'rain':       return <CloudRain className={className} />;
    case 'wind-out':
    case 'wind-in':
    case 'wind-cross': return <Wind className={className} />;
    case 'cold':       return <Snowflake className={className} />;
    case 'heat':       return <Sun className={className} />;
    case 'clear':      return <CloudSun className={className} />;
  }
}

export function formatWeatherLabel(sev: WeatherSeverityLabel): string {
  switch (sev.kind) {
    case 'indoor':     return 'Indoor';
    case 'rain':       return `${sev.probPct}%`;
    case 'wind-out':   return `${Math.round(sev.mph)} out`;
    case 'wind-in':    return `${Math.round(sev.mph)} in`;
    case 'wind-cross': return `${Math.round(sev.mph)}`;
    case 'cold':
    case 'heat':       return `${Math.round(sev.tempF)}°`;
    case 'clear':      return `${Math.round(sev.tempF)}°`;
  }
}

function severityHeadline(sev: WeatherSeverityLabel): string {
  switch (sev.kind) {
    case 'indoor':     return 'Indoor · controlled';
    case 'rain':       return `${sev.probPct}% rain`;
    case 'wind-out':   return `${Math.round(sev.mph)} mph blowing out`;
    case 'wind-in':    return `${Math.round(sev.mph)} mph blowing in`;
    case 'wind-cross': return `${Math.round(sev.mph)} mph crosswind`;
    case 'cold':       return `Cold · feels ${Math.round(sev.feelsLikeF)}°`;
    case 'heat':       return `Hot · feels ${Math.round(sev.feelsLikeF)}°`;
    case 'clear':      return `Clear · ${Math.round(sev.tempF)}°`;
  }
}

type WeatherPillSize = 'sm' | 'xs' | '2xs';

/** Single source of truth for the pill's size-derived classes. Shared by the real
 *  trigger AND the placeholder so their box dimensions can never drift — the
 *  placeholder reserves the pill's EXACT height by construction (no magic px). */
function pillSizeClasses(size: WeatherPillSize) {
  return {
    icon: size === 'sm' ? 'w-3 h-3' : size === '2xs' ? 'w-2 h-2' : 'w-2.5 h-2.5',
    text: size === 'sm' ? 'text-[11px]' : size === '2xs' ? 'text-[9px]' : 'text-[10px]',
    pad: size === '2xs' ? 'px-1 py-0' : 'px-1.5 py-0.5',
  };
}

interface WeatherTriggerProps {
  weather: GameWeather;
  size?: WeatherPillSize;
  className?: string;
}

/** Compact pill trigger — visual only. Popover/drawer behavior is supplied by RichPopover. */
export function WeatherTrigger({ weather, size = 'xs', className }: WeatherTriggerProps) {
  const sev = pickSeverity(weather);
  const sz = pillSizeClasses(size);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded-md border border-gray-200 dark:border-border-primary bg-white dark:bg-bg-tertiary font-medium flex-shrink-0 cursor-pointer select-none',
        'transition-colors hover:bg-gray-50 dark:hover:bg-bg-overlay',
        sz.pad,
        sz.text,
        severityTextClass(sev.kind),
        className,
      )}
    >
      <LabelIcon kind={sev.kind} className={sz.icon} />
      <span>{formatWeatherLabel(sev)}</span>
    </span>
  );
}

/**
 * Invisible, same-dimensioned stand-in for {@link WeatherTrigger}. Reserves the
 * pill's EXACT height — same `pillSizeClasses` → identical icon + text-line +
 * padding + border box — so the real pill (which arrives via a SEPARATE async
 * weather fetch, see useMlbWeather) drops into pre-allocated space with zero
 * layout shift. Replaces the brittle hardcoded `min-h-[Npx]`: nothing to keep in
 * sync when the pill's size changes.
 */
export function WeatherPillPlaceholder({ size = 'xs', className }: { size?: WeatherPillSize; className?: string }) {
  const sz = pillSizeClasses(size);
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex items-center gap-0.5 rounded-md border border-transparent font-medium flex-shrink-0 invisible',
        sz.pad,
        sz.text,
        className,
      )}
    >
      <LabelIcon kind="clear" className={sz.icon} />
      <span>{' '}</span>
    </span>
  );
}

export function WeatherDrawerHeader({ weather }: { weather: GameWeather }) {
  const sev = pickSeverity(weather);
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className={cn('inline-flex items-center justify-center w-6 h-6 rounded-md flex-shrink-0', labelClasses(sev.kind))}>
        <LabelIcon kind={sev.kind} className="w-3.5 h-3.5" />
      </span>
      <div className="flex flex-col min-w-0">
        <span className="text-sm font-semibold leading-tight truncate text-fg dark:text-text-primary">Weather</span>
        <span className="text-[11px] text-muted-foreground leading-tight truncate">{severityHeadline(sev)}</span>
      </div>
    </div>
  );
}
